/**
 * German UI catalog (NF-021, DE-111/ADR-FE-003). KEYS are ASCII; VALUES are
 * end-user-facing German product text and MAY use real Umlauts (the sole
 * ASCII-rule exception). Covers the shell + auth (Stage A) and the M-002 list /
 * M-004 create / M-005 detail screens (Stage B, incidents feature-module).
 */
export const de = {
  app: {
    title: "RYD Incident Tool",
    loading: "Wird geladen...",
  },
  account: {
    // User menu (CD-009 / LP-031): the way to the own account, colour mode
    // (CD-002) and build information (CD-010).
    menu: "Benutzermenü",
    ownAccount: "Mein Konto",
    save: "Speichern",
    theme: "Farbmodus",
    themeSystem: "Wie das System",
    themeLight: "Hell",
    themeDark: "Dunkel",
    themeHighContrast: "Hoher Kontrast",
    about: "Über diese Anwendung",
    aboutTitle: "RYD Incident Tool",
    aboutHint: "Version und Quellstand der laufenden Anwendung.",
    imprint: "Impressum",
    web: "Anwendung",
    api: "Server",
    version: "Version",
    build: "Build",
    builtAt: "Erstellt",
    source: "Quellstand",
    local: "lokal",
    // CD-017: both blocks onto the clipboard, for a report.
    copy: "In die Zwischenablage kopieren",
    copied: "Versionsangaben in die Zwischenablage kopiert.",
    copyFailed: "Kopieren ist nicht gelungen. Bitte die Angaben markieren und von Hand kopieren.",
  },
  // Own accounts (F-029): login, first login, password reset.
  auth: {
    signIn: "Anmelden",
    signOut: "Abmelden",
    subtitle:
      "Strukturierte Erfassung und Nachverfolgung betrieblicher Vorfälle.",
    helpLine: "Probleme bei der Anmeldung? Wenden Sie sich an die Administration.",
    roles: "Rollen",
    noRoles: "Keine Rolle zugewiesen",
  },
  login: {
    email: "Mailadresse",
    password: "Passwort",
    code: "Code",
    codeLead:
      "Ein achtstelliger Code ist an die Adresse des Systemkontos unterwegs. Erst mit ihm beginnt die Sitzung.",
    codeSubmit: "Code bestätigen",
    back: "Zurück",
    forgotten: "Passwort vergessen?",
    failed: "Die Anmeldung ist nicht gelungen. Bitte erneut versuchen.",
    noSession: "Die Anmeldung wurde angenommen, aber es besteht keine Sitzung. Bitte erneut anmelden.",
    sessionEnded: "Ihre Sitzung ist beendet. Bitte melden Sie sich erneut an.",
    loggedOut: "Sie sind abgemeldet.",
  },
  firstLogin: {
    title: "Zugang einrichten",
    lead:
      "Willkommen. Vergeben Sie Ihren Anzeigenamen und Ihr Passwort. Der Verweis aus der Mail lässt sich genau einmal verwenden.",
    displayName: "Anzeigename",
    displayNameHint:
      "So erscheinen Sie in Listen, an Vorfällen und in Exporten. Die Mailadresse sehen nur Sie und die Administration.",
    password: "Passwort",
    passwordRepeat: "Passwort wiederholen",
    submit: "Zugang einrichten",
    done: "Ihr Zugang ist eingerichtet. Melden Sie sich jetzt mit Ihrer Mailadresse und dem neuen Passwort an.",
    toLogin: "Zur Anmeldung",
    incomplete:
      "Dieser Verweis ist unvollständig. Bitte öffnen Sie ihn unverändert aus der Mail; wenn das nicht geht, lassen Sie sich von der Administration eine neue Einladung senden.",
    failed: "Der Zugang konnte nicht eingerichtet werden. Bitte erneut versuchen.",
  },
  reset: {
    title: "Passwort zurücksetzen",
    lead: "Geben Sie Ihre Mailadresse ein. Besteht dazu ein Konto, erhalten Sie einen achtstelligen Code.",
    sent:
      "Besteht für {{email}} ein Konto, ist ein achtstelliger Code dorthin unterwegs. Mit ihm setzen Sie ein neues Passwort.",
    request: "Code anfordern",
    code: "Code aus der Mail",
    password: "Neues Passwort",
    passwordRepeat: "Neues Passwort wiederholen",
    confirm: "Passwort setzen",
    again: "Andere Adresse eingeben",
    done: "Ihr Passwort ist neu gesetzt. Alle bisherigen Anmeldungen des Kontos sind beendet.",
    toLogin: "Zur Anmeldung",
    back: "Zurück zur Anmeldung",
    failed: "Das Zurücksetzen ist nicht gelungen. Bitte erneut versuchen.",
  },
  forms: {
    password: {
      hint: "Mindestens zehn Zeichen.",
      short: "Das Passwort braucht mindestens zehn Zeichen.",
      mismatch: "Die beiden Passwörter stimmen nicht überein.",
    },
    displayName: {
      short: "Der Anzeigename braucht mindestens vier Zeichen.",
      long: "Der Anzeigename darf höchstens 32 Zeichen haben.",
      takeSuggestion: "„{{name}}“ übernehmen",
    },
  },
  // Step-up (F-033): one dialog for every critical operation.
  elevation: {
    title: "Höhergestufte Sitzung",
    lead:
      "Dieser Vorgang betrifft Zugangsdaten oder Konten und verlangt eine höhergestufte Sitzung. Ein Einmalcode geht an {{email}}; danach arbeiten Sie für ein Zeitfenster ohne weitere Abfrage.",
    sent: "Der achtstellige Code ist an {{email}} unterwegs. Geben Sie ihn hier ein.",
    request: "Code anfordern",
    code: "Code",
    confirm: "Bestätigen",
    resend: "Erneut senden",
    requestFailed: "Der Code konnte nicht angefordert werden.",
    confirmFailed: "Der Code wurde nicht angenommen.",
  },
  // The own account (F-029, F-033, NF-008).
  ownAccount: {
    title: "Mein Konto",
    displayName: "Anzeigename",
    email: "Mailadresse",
    sessionEnds: "Diese Sitzung endet",
    elevation: "Höherstufung",
    elevatedUntil: "höhergestuft bis {{time}}",
    notElevated: "nicht höhergestuft",
    elevate: "Sitzung höherstufen",
    displayNameTitle: "Anzeigename",
    displayNameLead:
      "So sehen Sie andere - in Listen, an Vorfällen und in Exporten, auch an bestehenden Einträgen. Ändern verlangt keine Höherstufung.",
    displayNameSaved: "Anzeigename gespeichert.",
    passwordTitle: "Passwort ändern",
    passwordLead:
      "Verlangt eine höhergestufte Sitzung. Alle anderen Anmeldungen Ihres Kontos werden danach beendet.",
    newPassword: "Neues Passwort",
    passwordRepeat: "Neues Passwort wiederholen",
    passwordSave: "Passwort ändern",
    passwordSaved: "Das Passwort ist geändert. Andere Anmeldungen sind beendet.",
    emailTitle: "Mailadresse ändern",
    emailLead:
      "Die Mailadresse ist zugleich Ihr Anmeldename. Der Wechsel verlangt eine höhergestufte Sitzung und je einen Code an die bisherige und an die neue Adresse.",
    newEmail: "Neue Mailadresse",
    emailRequest: "Codes anfordern",
    emailSent: "Je ein achtstelliger Code ist an {{old}} und an {{target}} unterwegs.",
    codeOld: "Code an die bisherige Adresse",
    codeNew: "Code an die neue Adresse",
    emailConfirm: "Wechsel bestätigen",
    emailSaved: "Die Mailadresse ist geändert. Andere Anmeldungen sind beendet.",
    sessionsTitle: "Meine Anmeldungen",
    sessionsLead:
      "Jede Anmeldung endet spätestens zwölf Stunden nach ihrem Beginn. Eine Anmeldung, die Sie nicht kennen, beenden Sie hier.",
  },
  sessions: {
    device: "Gerät",
    since: "Angemeldet",
    lastSeen: "Zuletzt genutzt",
    endsAt: "Endet",
    current: "diese Sitzung",
    elevated: "höhergestuft",
    revoke: "Beenden",
    revoked: "Die Anmeldung ist beendet.",
    none: "Keine laufende Anmeldung.",
  },
  // The system account (F-031).
  system: {
    name: "Systemkonto",
    role: "Notzugang",
    noticeTitle: "Angemeldet als Systemkonto",
    noticeBody:
      "Das Systemkonto verwaltet Konten und arbeitet an keinem Inhalt. Seine Sitzung endet mit dem Fenster der Höherstufung.",
  },
  session: {
    expiryTitle: "Ihre Sitzung endet bald",
    idleBody:
      "Die Sitzung endet nach längerer Untätigkeit in {{countdown}} Minuten. Ungespeicherte Eingaben gehen dabei verloren.",
    absoluteBody:
      "Die Sitzung erreicht in {{countdown}} Minuten ihre Höchstdauer und endet. Danach melden Sie sich erneut an; was bis dahin gespeichert ist, bleibt.",
    systemBody:
      "Die Sitzung des Systemkontos endet in {{countdown}} Minuten mit dem Fenster der Höherstufung.",
    extend: "Sitzung verlängern",
  },
  nav: {
    dashboard: "Übersicht",
    incidents: "Vorfälle",
    newIncident: "Neuer Vorfall",
    createIncident: "Vorfall erfassen",
    privacy: "Datenschutz",
    users: "Benutzer",
    status: "Status",
    journal: "Kontoprotokoll",
    masterData: "Stammdaten",
    trends: "Trend-Dashboard",
    operationsLog: "Betriebslog",
    menu: "Menü",
  },
  // Units of a duration (status page, NF-019).
  duration: {
    day: "{{n}} Tag",
    days: "{{n}} Tage",
    hour: "{{n}} Stunde",
    hours: "{{n}} Stunden",
    minute: "{{n}} Minute",
    minutes: "{{n}} Minuten",
    second: "{{n}} Sekunde",
    seconds: "{{n}} Sekunden",
  },
  roleLabels: {
    melder: "Melder",
    bearbeiter: "Bearbeiter",
    incident_owner: "Incident Owner",
    manager: "Manager",
    admin: "Administrator",
    dsgvo_auditor: "DSGVO-Auditor (Arbeitstitel)",
  },
  notifications: {
    headingOne: "1 Erinnerung",
    headingMany: "{{count}} Erinnerungen",
    openIncident: "Zum Vorfall",
    dismiss: "Erinnerung verwerfen",
  },
  common: {
    retry: "Erneut laden",
    tryAgain: "Erneut versuchen",
    loadMore: "Mehr laden",
    loading: "Wird geladen...",
    none: "-",
    cancel: "Abbrechen",
    close: "Schließen",
    back: "Zurück",
  },
  errors: {
    generic: "Es ist ein Fehler aufgetreten.",
    load: "Die Daten konnten nicht geladen werden.",
    saveFailed: "Speichern fehlgeschlagen.",
    forbidden: "Sie haben keine Berechtigung für diese Aktion.",
    conflict: "Die Aktion ist nicht möglich.",
    precondition: "Jemand war schneller - bitte neu laden.",
    // Postmortem problem keys (RFC 7807 title -> localized message).
    postmortemStatusInvalid:
      "Das Postmortem kann im aktuellen Vorfall-Status nicht bearbeitet werden.",
    postmortemIncompleteForFinal:
      "Zum finalen Speichern müssen alle Pflichtabschnitte ausgefüllt sein. Als Entwurf lässt sich das Postmortem jederzeit speichern.",
    // Attachment problem keys (RFC 7807 title -> localized message).
    attachmentTypeNotAllowed:
      "Der Dateityp ist nicht zulässig. Erlaubt sind Bilder, Dokumente, Archive und Office-Dateien.",
    attachmentTooLarge:
      "Die Datei überschreitet die maximale Größe von 25 MB.",
    attachmentCountExceeded: "Pro Upload sind maximal 10 Dateien zulässig.",
    attachmentAlreadyRemoved: "Der Anhang wurde bereits entfernt.",
    attachmentGone: "Der Anhang wurde entfernt und ist nicht mehr abrufbar.",
    attachmentNotDownloadable:
      "Externe Links können nicht heruntergeladen werden.",
  },
  incidentStatus: {
    neu: "Neu",
    in_analyse: "In Analyse",
    in_bearbeitung: "In Bearbeitung",
    beobachtet: "Beobachtet",
    geloest: "Gelöst",
    abgeschlossen: "Abgeschlossen",
  },
  timelineType: {
    kommentar: "Kommentar",
    massnahme: "Maßnahme",
    externe_auswirkung: "Externe Auswirkung",
    statuswechsel: "Statuswechsel",
    rollenaenderung: "Rollenänderung",
    tag_aenderung: "Tag-Änderung",
    anhang_hinzugefuegt: "Anhang hinzugefügt",
    anhang_entfernt: "Anhang entfernt",
    postmortem_erstellt: "Postmortem erstellt",
    wiedereroeffnung: "Wiedereröffnung",
    erinnerung: "Erinnerung",
  },
  list: {
    title: "Vorfälle",
    countLine: "{{shown}} von {{total}} Vorfällen",
    searchLabel: "Vorfälle durchsuchen",
    searchPlaceholder: "Titel, Beschreibung oder Nummer",
    quickFilters: "Schnellfilter:",
    quickOpen: "Offen",
    quickOverdue: "Überfällig",
    quickMine: "Meine",
    quickArchived: "Mit Archiv",
    reset: "Zurücksetzen",
    resetFilters: "Filter zurücksetzen",
    empty: "Es sind noch keine Vorfälle vorhanden.",
    emptyFiltered: "Keine Vorfälle für die gewählte Filterung.",
    createFirst: "Vorfall erfassen",
    colNumber: "Nummer",
    colTitle: "Titel",
    colStatus: "Status",
    colSeverity: "Schweregrad",
    colCategory: "Kategorie",
    colOwner: "Incident Owner",
    colCreated: "Erstellt am",
    colDiscovered: "Entdeckt am",
    sortBy: "Nach {{column}} sortieren",
    sortLabel: "Sortierung",
    sortOptionAsc: "{{column}}, aufsteigend",
    sortOptionDesc: "{{column}}, absteigend",
    filterStatus: "Status",
    filterStatusCount: "{{count}} ausgewählt",
    inlineSaved: "Vorfall {{number}} aktualisiert.",
    filterSeverity: "Schweregrad",
    filterArea: "Bereich",
    filterTag: "Tag",
    filterOwner: "Incident Owner",
    filterDiscoveredFrom: "Entdeckt ab",
    filterDiscoveredTo: "Entdeckt bis",
    filterAll: "Alle",
    unassigned: "Nicht zugewiesen",
    activeFilters: "Filter:",
    chipCategory: "Kategorie: {{value}}",
    chipCreatedAfter: "Erstellt nach: {{value}}",
    chipMissingPostmortem: "Ohne Postmortem",
    chipOverdueFollowup: "Überfällige Follow-ups",
    chipRemove: "Filter entfernen",
  },
  dashboard: {
    title: "Übersicht",
    tiles: {
      open: "Offene Vorfälle",
      openPostmortems: "Offene Postmortems",
      overdue: "Überfällige Follow-ups",
      last7: "Letzte 7 Tage",
      last30ByCategory: "Letzte 30 Tage nach Kategorie",
      noCategory: "Ohne Kategorie",
    },
    trend: {
      title: "Vorfälle im Zeitverlauf",
      bucketLabel: "Zeitraster",
      bucketWeek: "Woche",
      bucketMonth: "Monat",
      bucketQuarter: "Quartal",
      groupLabel: "Gruppierung",
      groupCategory: "Kategorie",
      groupArea: "Bereich",
      groupTag: "Tag",
      rangeLabel: "Zeitraum",
      range6: "6 Monate",
      range12: "12 Monate",
      range24: "24 Monate",
      empty: "Keine Vorfälle im gewählten Zeitraum.",
      showTable: "Als Tabelle anzeigen",
      showChart: "Als Diagramm anzeigen",
      unassigned: "Ohne Zuordnung",
      others: "Sonstige",
      chartLabel: "Balkendiagramm der Vorfälle im Zeitverlauf",
      tableBucket: "Zeitraum",
      countLabel: "Anzahl",
    },
    combos: {
      title: "Häufige Tag-Kombinationen",
      tooFew:
        "Noch zu wenige Vorfälle mit Tags für eine belastbare Auswertung.",
      one: "1 Vorfall",
      many: "{{count}} Vorfälle",
    },
  },
  trends: {
    title: "Trend-Dashboard",
    subtitle:
      "Häufungen im Zeitverlauf und Tags, die gemeinsam auftreten. Rein lesend.",
    teaser: "Häufungen im Zeitverlauf und häufige Tag-Kombinationen.",
    open: "Trend-Dashboard öffnen",
    windowLabel: "Ausgewertete Vorfälle",
    windowOption: "Die letzten {{count}} Vorfälle",
  },
  create: {
    title: "Neuen Vorfall anlegen",
    subtitle:
      "In unter einer Minute erfasst. Pflichtfelder sind mit * markiert.",
    fieldTitle: "Titel",
    fieldDescription: "Beschreibung",
    fieldCategory: "Kategorie",
    fieldSeverity: "Schweregrad",
    fieldAreas: "Betroffene Bereiche",
    fieldTags: "Tags",
    fieldDiscoveredAt: "Entdeckt am",
    fieldDetectedVia: "Erkannt durch",
    fieldReportedBy: "Gemeldet von",
    titlePlaceholder: "Kurze, prägnante Bezeichnung",
    descriptionPlaceholder: "Was ist passiert? Was ist die Auswirkung?",
    areasHint: "Mehrfachauswahl möglich",
    quickNow: "Jetzt",
    quickToday: "Heute",
    moreDetails: "Mehr Details (optional)",
    reportedByHint:
      "Vorbelegt mit Ihrem Konto - ändern Sie es, wenn Sie den Vorfall für eine andere Person erfassen.",
    selectPlaceholder: "Bitte wählen",
    submit: "Vorfall anlegen",
    submitting: "Wird angelegt...",
    createdToast: "Vorfall angelegt.",
    validationSummary: "Bitte prüfen Sie die markierten Felder ({{count}}).",
    createdNotice: "Vorfall {{number}} angelegt.",
    createdDismiss: "Hinweis schließen",
    successOpen: "Vorfall öffnen",
    validation: {
      titleRequired: "Bitte geben Sie einen Titel ein.",
      titleMax: "Der Titel darf höchstens 200 Zeichen lang sein.",
      descriptionRequired: "Bitte geben Sie eine Beschreibung ein.",
      categoryRequired: "Bitte wählen Sie eine Kategorie.",
      severityRequired: "Bitte wählen Sie einen Schweregrad.",
      areasRequired: "Bitte wählen Sie mindestens einen Bereich.",
      discoveredAtRequired: "Bitte geben Sie den Entdeckungszeitpunkt an.",
      detectedViaRequired: "Bitte geben Sie an, wie der Vorfall erkannt wurde.",
      reportedByRequired: "Bitte geben Sie an, wer den Vorfall gemeldet hat.",
    },
  },
  // F-004: who may be named Owner or Bearbeiter. The others stay visible in
  // the pickers, disabled, with this reason (AC-004).
  incidentParty: {
    notEligible: "{{name}} - weder Incident Owner noch Bearbeiter",
  },
  detail: {
    backToList: "Zurück zur Liste",
    description: "Beschreibung",
    category: "Kategorie",
    severity: "Schweregrad",
    areas: "Betroffene Bereiche",
    tags: "Tags",
    owner: "Incident Owner",
    assignees: "Bearbeiter",
    reporter: "Gemeldet von",
    discoveredAt: "Entdeckt am",
    detectedVia: "Erkannt durch",
    createdAt: "Erstellt am",
    closedAt: "Abgeschlossen am",
    notAssigned: "Nicht zugewiesen",
    changeStatus: "Status ändern",
    statusChanged: "Status geändert.",
    assignment: "Zuweisung",
    ownerNone: "- kein Owner -",
    ownerAssigned: "Incident Owner aktualisiert.",
    saveAssignees: "Bearbeiter speichern",
    assigneesSaved: "Bearbeiter gespeichert.",
    timeline: "Verlauf",
    timelineCountOne: "Verlauf · 1 Eintrag",
    timelineCountMany: "Verlauf · {{count}} Einträge",
    timelineFilterAll: "Alle",
    timelineFilterManual: "Manuell",
    timelineFilterSystem: "System",
    timelineEmpty: "Noch keine Einträge vorhanden.",
    addEntry: "Eintrag hinzufügen",
    addEntrySubmit: "Hinzufügen",
    entryPlaceholder: "Ihr Eintrag ...",
    entryAdded: "Eintrag hinzugefügt.",
    composerImmutable:
      "Einträge sind nach dem Speichern unveränderlich. Andere Einträge als Kommentare schreiben der Owner und die zugewiesenen Bearbeiter.",
    ownerMissing:
      "Diesem Vorfall ist noch kein Incident Owner zugewiesen. Ohne Owner lässt sich der Status nicht ändern.",
    take: "Vorfall übernehmen",
    taken: "Sie sind jetzt Owner dieses Vorfalls.",
    moreActions: "Weitere Aktionen",
    metaTitle: "Details",
    addTag: "Tag hinzufügen",
    removeTag: "Tag {{name}} entfernen",
    tagsUpdated: "Tags aktualisiert.",
    by: "von",
    edit: {
      action: "Bearbeiten",
      title: "Vorfall bearbeiten",
      intro:
        "Titel, Beschreibung, Kategorie, Schweregrad und betroffene Bereiche. Jede Änderung wird im Audit-Log erfasst.",
      inactiveSuffix: "(deaktiviert)",
      save: "Änderungen speichern",
      saving: "Wird gespeichert ...",
      saved: "Vorfall aktualisiert.",
    },
  },
  reopen: {
    action: "Wieder öffnen",
    dialogTitle: "Vorfall wieder öffnen",
    dialogIntro:
      "Der Vorfall wird in den Status In Bearbeitung zurückversetzt. Bitte begründen Sie die Wiedereröffnung.",
    reason: "Begründung",
    reasonHint: "Mindestens 10 Zeichen ({{chars}}/10)",
    adminCorrection: "Admin-Korrektur (zählt nicht gegen die Obergrenze)",
    confirm: "Wieder öffnen",
    submitting: "Wird wiedereröffnet ...",
    done: "Vorfall wiedereröffnet.",
    escalationTitle: "Mehrfache Wiedereröffnung",
    escalationHint:
      "Dieser Vorfall wurde bereits {{count}}-mal wiedereröffnet. Mehrfache Wiedereröffnung deutet auf ein strukturelles Problem hin - bitte Manager und Geschäftsführung einbeziehen.",
  },
  legalHold: {
    badge: "Rechtlicher Vermerk",
    setAction: "Vermerk setzen",
    setTitle: "Rechtlichen Vermerk setzen",
    setIntro:
      "Schützt den Vorfall vor der automatischen Löschung von Anhängen und vor der Archivierung nach Ablauf der Aufbewahrungsfristen (rechtlicher/vertraglicher Vorgang).",
    reason: "Begründung",
    reasonHint: "Mindestens 10 Zeichen ({{chars}}/10)",
    setConfirm: "Vermerk setzen",
    setting: "Wird gesetzt ...",
    setDone: "Rechtlicher Vermerk gesetzt.",
    clearAction: "Vermerk aufheben",
    clearTitle: "Rechtlichen Vermerk aufheben",
    clearIntro:
      "Der Vorfall fällt danach wieder unter die automatische Löschung von Anhängen und die Archivierung nach Ablauf der Aufbewahrungsfristen.",
    storedReason: "Hinterlegte Begründung",
    clearConfirm: "Vermerk aufheben",
    clearing: "Wird aufgehoben ...",
    clearDone: "Rechtlicher Vermerk aufgehoben.",
  },
  // GR-004: archived three years after closing; nothing is deleted.
  archive: {
    badge: "Archiviert",
    badgeTitle:
      "Archiviert am {{date}}, drei Jahre nach dem Abschluss (Aufbewahrungsfrist). Der Vorfall bleibt vollständig lesbar.",
  },
  attachments: {
    title: "Anhänge & Links",
    empty: "Keine Anhänge vorhanden.",
    download: "Herunterladen",
    uploadedBy: "von",
    upload: "Dateien hochladen",
    uploadHint:
      "Max. 10 Dateien pro Upload, je max. 25 MB. Erlaubt: Bilder, Dokumente, Archive, Office-Dateien.",
    uploading: "Wird hochgeladen ...",
    uploaded: "Anhänge hochgeladen.",
    tooLarge: "Datei zu groß (max. 25 MB): {{files}}",
    tooMany: "Bitte wählen Sie höchstens 10 Dateien pro Upload.",
    addLink: "Link hinzufügen",
    linkUrl: "URL",
    linkUrlPlaceholder: "https://...",
    linkDescription: "Beschreibung (optional)",
    linkUrlInvalid:
      "Bitte geben Sie eine gültige URL an (http:// oder https://).",
    linkSubmit: "Link hinzufügen",
    linkAdding: "Wird hinzugefügt ...",
    linkAdded: "Link hinzugefügt.",
    remove: "Entfernen",
    removeTitle: "Anhang entfernen",
    removeIntro:
      "Der Inhalt von „{{name}}“ wird dauerhaft entfernt. Der Eintrag bleibt mit einem Entfernungsvermerk sichtbar.",
    removeReason: "Begründung",
    removeReasonHint: "Mindestens 10 Zeichen ({{chars}}/10)",
    removeConfirm: "Anhang entfernen",
    removing: "Wird entfernt ...",
    removed: "Anhang entfernt.",
    removedNotice: "Anhang entfernt durch {{name}} am {{date}}: {{reason}}",
    removedNoticeRetention:
      "Anhang nach Ablauf der Aufbewahrungsfrist entfernt am {{date}} ({{reason}})",
    previewAlt: "Vorschau von {{name}}",
  },
  dsgvo: {
    title: "DSGVO-Auskunft & Löschung",
    subtitle:
      "Auskunft (Art. 15) und Löschung als Anonymisierung (Art. 17).",
    tabMyData: "Meine Daten",
    tabManage: "Anfragen verwalten",
    myData: {
      intro:
        "Übersicht der zu Ihrer Person gespeicherten personenbezogenen Datenkategorien.",
      account: "Benutzerkonto",
      email: "E-Mail",
      accountActive: "Aktiv",
      accountInactive: "Inaktiv",
      accountCreatedAt: "Konto angelegt am",
      categories: "Gespeicherte Datenkategorien",
      catIncidents: "Autorenschaft an Vorfällen",
      catTimeline: "Autorenschaft an Timeline-Einträgen",
      catPostmortems: "Postmortems",
      catAttachments: "Anhänge",
      catAudit: "Audit-Referenzen",
      countOne: "1 Eintrag",
      countMany: "{{count}} Einträge",
      export: "Datenauskunft exportieren",
      exportRunning: "Wird erstellt ...",
      exportDone: "Auskunft exportiert.",
      exportError: "Der Export ist fehlgeschlagen.",
      auditNotice:
        "Jede Auskunft und jeder Löschvorgang wird revisionssicher protokolliert.",
    },
    manage: {
      roleActionHint:
        "Auskünfte erstellen, Löschungen beauftragen und die manuelle Prüfung bearbeiten kann nur die Rolle DSGVO-Auditor.",
      readOnlyHint:
        "Sie sehen diesen Bereich nur lesend. Löschungen beauftragen und die manuelle Prüfung bearbeiten kann nur die Rolle DSGVO-Auditor.",
      twoStagePromise:
        "Löschungen wirken sofort im Live-System; Backup-Kopien erlöschen innerhalb der operativen Rotationsfrist von ca. 6 Monaten.",
      searchTitle: "Betroffene Person suchen",
      searchPlaceholder: "Name oder Mailadresse ...",
      searchHint:
        "Die Suche findet auch ausgeschiedene und bereits anonymisierte Personen.",
      searchEmpty: "Keine Person gefunden.",
      badgeRetired: "Ausgeschieden",
      badgeUnconfirmed: "Eingeladen",
      badgeAnonymized: "Anonymisiert",
      badgeNoAccount: "Ohne Benutzerkonto",
      showOccurrences: "Fundstellen anzeigen",
      otherNames: "Auch gespeichert als: {{names}}",
    },
    person: {
      title: "Fundstellen zu {{name}}",
      summary:
        "{{total}} Fundstellen, davon {{freeText}} in Freitexten (manuell zu prüfen).",
      empty: "Zu dieser Person gibt es keine Fundstellen.",
      disclosure: "Auskunft erstellen",
      disclosureRunning: "Wird erstellt ...",
      disclosureDone: "Auskunft erstellt und im Löschjournal erfasst.",
      disclosureError: "Die Auskunft konnte nicht erstellt werden.",
      erase: "Löschung beauftragen",
      erasureOrdered:
        "Für diese Person ist bereits eine Löschung beauftragt. Sie wird über das Löschjournal und die manuelle Prüfung abgeschlossen.",
      anonymizedHint: "Diese Person ist bereits anonymisiert.",
      noAccountHint:
        "Dieser Eintrag ist kein Benutzerkonto und kann nicht anonymisiert werden.",
      freeText: "Manuell zu prüfen",
      groupCountOne: "1 Fundstelle",
      groupCountMany: "{{count}} Fundstellen",
      incident: "Vorfall {{number}}",
      noContent: "(ohne Inhalt)",
      close: "Schließen",
    },
    dataObject: {
      incident: "Vorfall",
      timeline_event: "Timeline-Eintrag",
      attachment: "Anhang",
      postmortem: "Postmortem",
      followup_task: "Follow-up-Task",
      operations_log: "Betriebslog-Eintrag",
      input_draft: "Entwurf",
      audit_log: "Audit-Log",
    },
    field: {
      reported_by: "Gemeldet von",
      owner: "Owner",
      assignee: "Bearbeiter",
      author: "Verfasst von",
      uploaded_by: "Hochgeladen von",
      removed_by: "Entfernt von",
      assigned_to: "Zugewiesen an",
      recorded_by: "Erfasst von",
      saved_by: "Gespeichert von",
      actor: "Handelnde Person",
      entity: "Eintrag zum Benutzerkonto",
      title: "Titel",
      description: "Beschreibung",
      detected_via: "Erkannt durch",
      legal_hold_reason: "Begründung der rechtlichen Kennzeichnung",
      text: "Text",
      cause: "Ursache",
      affected_components: "Betroffene Komponenten",
      resolution: "Lösung",
      prevention: "Präventionsmaßnahme",
      positive_learnings: "Was hat gut funktioniert?",
      earlier_detectable: "Was hätten wir früher erkennen können?",
      filename_or_url: "Dateiname oder Link",
      removal_reason: "Begründung der Entfernung",
      short_description: "Kurzbeschreibung",
      old_value: "Alter Wert",
      new_value: "Neuer Wert",
    },
    journal: {
      title: "Löschjournal",
      empty: "Noch keine Auskunft und keine Löschung.",
      colKind: "Vorgang",
      colSubject: "Betroffene Person",
      colRequestedAt: "Zeitpunkt",
      colScope: "Umfang",
      colStatus: "Status",
      colUpdated: "Letzte Aktualisierung",
      colActions: "Aktionen",
      kind: {
        erasure: "Löschung",
        disclosure: "Auskunft",
      },
      requestedBy: "durch {{name}}",
      scope: "{{count}} Fundstellen",
      status: {
        pending: "Beauftragt",
        applied: "Ausgeführt",
        replayed: "Nach Restore erneut ausgeführt",
        failed: "Fehlgeschlagen",
      },
      reviewOpen: "Prüfung offen: {{open}} von {{total}}",
      reviewResolved: "Prüfung abgeschlossen",
      backupUntil: "Backup-Restfrist bis {{date}}",
      lastError: "Fehler: {{error}}",
      replay: "Erneut ausführen",
      replaying: "Wird ausgeführt ...",
      replayDone: "Löschung erneut ausgeführt.",
    },
    review: {
      title: "Manuelle Prüfung",
      intro:
        "Freitexte, in denen die gelöschte Person genannt ist. Jede Fundstelle wird bearbeitet oder unverändert bestätigt; danach wird die Prüfung mit einer Notiz abgeschlossen.",
      entry: "{{name}} - Löschung vom {{date}}",
      progress: "{{open}} von {{total}} Fundstellen offen",
      show: "Fundstellen prüfen",
      hide: "Ausblenden",
      noItems:
        "Keine Freitext-Fundstelle gefunden. Die Prüfung kann mit einer Notiz abgeschlossen werden.",
      edit: "Bearbeiten",
      confirm: "Unverändert bestätigen",
      confirmed: "Fundstelle bestätigt.",
      itemStatus: {
        open: "Offen",
        edited: "Bearbeitet",
        confirmed: "Bestätigt",
      },
      resolvedBy: "durch {{name}} am {{date}}",
      editTitle: "Fundstelle bearbeiten",
      editIntro:
        "Ersetzen Sie den Personenbezug, etwa durch den Platzhalter oder eine Rolle. Der Datensatz behält Zeitpunkt und Position.",
      editJsonHint:
        "Dieser Wert des Audit-Logs ist JSON und muss gültiges JSON bleiben.",
      editRequiredHint: "Dieses Feld darf nicht leer sein.",
      editText: "Neuer Text",
      save: "Speichern",
      saving: "Wird gespeichert ...",
      saved: "Fundstelle bearbeitet.",
      resolve: "Prüfung abschließen",
      resolveBlocked: "Zuerst alle Fundstellen bearbeiten oder bestätigen.",
      dialogTitle: "Manuelle Prüfung abschließen",
      dialogIntro:
        "Dokumentieren Sie das Ergebnis der manuellen Prüfung. Die Notiz wird revisionssicher protokolliert.",
      note: "Ergebnisnotiz",
      noteHint: "Mindestens 10 Zeichen ({{chars}}/10)",
      resolving: "Wird gespeichert ...",
      resolved: "Manuelle Prüfung abgeschlossen.",
    },
    dialog: {
      title: "Löschung beauftragen",
      intro:
        "Die Löschung ist eine Anonymisierung und unumkehrbar: Name und Mailadresse werden durch einen dauerhaften Platzhalter ersetzt, überall, wo die Person genannt ist. Inhalte, Zeitstempel und Reihenfolge bleiben erhalten.",
      subject: "Betroffene Person",
      colField: "Feld",
      colDisposition: "Änderung",
      colHint: "Hinweis",
      disposition: {
        tombstone: "Platzhalter",
        hash_retain: "Hash behalten",
        purge: "Entfernen",
      },
      scopeTitle: "Fundstellen",
      scopeSummary:
        "{{total}} Fundstellen; {{freeText}} Freitext-Fundstellen gehen in die manuelle Prüfung und werden nicht automatisch geändert.",
      confirmCheckbox: "Ich habe die Fundstellen und die Änderungen geprüft",
      confirm: "Löschung beauftragen",
      submitting: "Wird beauftragt ...",
      ordered: "Löschung ausgeführt. Die manuelle Prüfung ist offen.",
      failedHint:
        "Ist der Vorgang im Löschjournal als fehlgeschlagen vermerkt, kann er dort erneut ausgeführt werden.",
      elevate: "Sitzung höherstufen",
    },
  },
  // Betriebslog (F-015, F-013; Hauptansicht 5 in SRS 4.1).
  operationsLog: {
    title: "Betriebslog",
    subtitle:
      "Systemänderungen, Wartungen und besondere Ereignisse - chronologisch, damit sich Vorfälle zeitlich einordnen lassen.",
    countLine: "{{shown}} von {{total}} Einträgen",
    create: "Eintrag erfassen",
    type: {
      systemaenderung: "Systemänderung",
      wartung: "Wartung",
      besonderes_ereignis: "Besonderes Ereignis",
    },
    searchLabel: "Betriebslog durchsuchen",
    searchPlaceholder: "Kurzbeschreibung",
    filterType: "Typ",
    filterArea: "Bereich",
    filterAll: "Alle",
    reset: "Filter zurücksetzen",
    colStart: "Zeitpunkt",
    colType: "Typ",
    colAreas: "Bereiche",
    colDescription: "Kurzbeschreibung",
    colRecordedBy: "Erfasst von",
    colIncidents: "Vorfälle",
    empty: "Noch keine Einträge im Betriebslog.",
    emptyFiltered: "Keine Einträge für die gewählte Filterung.",
    period: "{{start}} bis {{end}}",
    incidentsOne: "1 Vorfall",
    incidentsMany: "{{count}} Vorfälle",
    detail: {
      back: "Zurück zum Betriebslog",
      start: "Beginn",
      end: "Ende",
      areas: "Betroffene Bereiche",
      recordedBy: "Erfasst von",
      recordedAt: "Erfasst am",
      description: "Kurzbeschreibung",
      incidents: "Verknüpfte Vorfälle",
      noIncidents: "Mit keinem Vorfall verknüpft.",
      linkHint:
        "Verknüpft wird am Vorfall: Owner und zugewiesene Bearbeiter verknüpfen ihn dort mit diesem Eintrag.",
    },
    dialog: {
      title: "Betriebslog-Eintrag erfassen",
      intro:
        "Pflichtfelder sind mit * markiert. Einträge lassen sich nach dem Speichern nicht mehr ändern.",
      type: "Typ",
      typePlaceholder: "Bitte wählen",
      start: "Zeitpunkt (Beginn)",
      end: "Ende",
      endHint: "Optional, etwa für ein Wartungsfenster.",
      now: "Jetzt",
      areas: "Betroffene Bereiche",
      description: "Kurzbeschreibung",
      descriptionPlaceholder: "Was wurde geändert oder ist passiert?",
      submit: "Eintrag speichern",
      submitting: "Wird gespeichert ...",
      created: "Betriebslog-Eintrag erfasst.",
    },
    validation: {
      typeRequired: "Bitte wählen Sie einen Typ.",
      startRequired: "Bitte geben Sie den Zeitpunkt an.",
      endInvalid: "Bitte geben Sie ein gültiges Ende an.",
      endBeforeStart: "Das Ende darf nicht vor dem Beginn liegen.",
      areasRequired: "Bitte wählen Sie mindestens einen Bereich.",
      descriptionRequired: "Bitte geben Sie eine Kurzbeschreibung ein.",
    },
    // The card on the incident detail page (Hauptansicht 3).
    card: {
      title: "Betriebslog",
      empty: "Mit keinem Betriebslog-Eintrag verknüpft.",
      link: "Eintrag verknüpfen",
      linkTitle: "Betriebslog-Eintrag verknüpfen",
      linkIntro:
        "Wählen Sie den Eintrag aus dem Betriebslog, der mit diesem Vorfall zusammenhängt. Die Verknüpfung ist an beiden Stellen sichtbar.",
      searchLabel: "Einträge durchsuchen",
      searchPlaceholder: "Kurzbeschreibung ...",
      linkAction: "Verknüpfen",
      linking: "Wird verknüpft ...",
      linked: "Betriebslog-Eintrag verknüpft.",
      alreadyLinked: "Bereits verknüpft",
      noCandidates: "Keine passenden Einträge im Betriebslog.",
      close: "Schließen",
    },
  },
  adminUsers: {
    title: "Benutzer & Rollen",
    countLine: "{{shown}} von {{total}} Konten - {{active}} aktiv",
    lead:
      "Konten legt die Administration an. Die Person erhält eine Einladung per Mail und setzt Passwort und Anzeigenamen selbst; bis dahin ist das Konto unbestätigt. Konten werden stillgelegt, nie gelöscht.",
    auditHint: "Jede Änderung an Konten und Rollen steht unveränderbar im Kontoprotokoll.",
    toJournal: "Zum Kontoprotokoll",
    forbidden: "Dieser Bereich gehört der Administration.",
    toolbar: "Konten filtern und anlegen",
    searchLabel: "Konten suchen",
    searchPlaceholder: "Name oder Mailadresse ...",
    showRetired: "Stillgelegte anzeigen",
    empty: "Keine Konten gefunden.",
    noName: "(noch kein Anzeigename)",
    email: "Mailadresse",
    rolesLabel: "Rollen",
    lastRoleHint: "Mindestens eine Rolle ist erforderlich.",
    actionsFor: "Aktionen für {{name}}",
    states: {
      unconfirmed: "Unbestätigt",
      active: "Aktiv",
      retired: "Stillgelegt",
    },
    create: "Konto anlegen",
    createTitle: "Konto anlegen",
    createLead:
      "Mailadresse und Rollen genügen. Die Person erhält eine Einladung und setzt Passwort und Anzeigenamen selbst. Verlangt eine höhergestufte Sitzung.",
    createSubmit: "Anlegen und einladen",
    created: "Konto angelegt. Die Einladung ist an {{email}} unterwegs.",
    createdNotInvited:
      "Das Konto ist angelegt, aber die Einladung konnte nicht versandt werden. Der Mailweg ist gestört; senden Sie sie später erneut.",
    editRoles: "Rollen",
    rolesTitle: "Rollen bearbeiten",
    rolesLead:
      "Rollen von {{name}}. Eine Person kann mehrere Rollen halten und hat dann die Rechte aller. Verlangt eine höhergestufte Sitzung.",
    rolesSaved: "Rollen gespeichert.",
    resendInvite: "Einladung erneut",
    inviteSent: "Eine neue Einladung ist an {{email}} unterwegs; die vorherige gilt nicht mehr.",
    inviteNotSent: "Die Einladung konnte nicht versandt werden. Der Mailweg ist gestört.",
    sessions: "Anmeldungen",
    sessionsTitle: "Anmeldungen",
    sessionsLead: "Laufende Anmeldungen von {{name}}. Beendet wird sofort; die Person erfährt es per Mail.",
    recover: "Adresse wiederherstellen",
    recoverTitle: "Mailadresse wiederherstellen",
    recoverLead:
      "Die Adresse {{email}} von {{name}} ist nicht mehr erreichbar. Je ein Code geht an das Systemkonto und an die neue Adresse; die Änderung gilt erst mit beiden.",
    recoverNewEmail: "Neue Mailadresse",
    recoverRequest: "Codes anfordern",
    recoverSent: "Je ein achtstelliger Code ist an das Systemkonto und an {{email}} unterwegs.",
    recoverCodeSystem: "Code an das Systemkonto",
    recoverCodeNew: "Code an die neue Adresse",
    recoverConfirm: "Adresse ändern",
    recovered: "Die Mailadresse ist auf {{email}} geändert.",
    retire: "Stilllegen",
    reactivate: "Reaktivieren",
    retired: "Konto stillgelegt.",
    reactivated: "Konto reaktiviert.",
    roleHints: {
      melder: "Erfasst Hindernisse und kommentiert sie.",
      bearbeiter: "Arbeitet an der Lösung eines Vorfalls.",
      incident_owner: "Übernimmt Vorfälle und verantwortet sie bis zum Abschluss.",
      manager: "Wertet aus, rein lesend.",
      admin: "Verwaltet das Werkzeug - Konten, Rollen und Stammdaten -, nicht die Vorfälle.",
      dsgvo_auditor: "Bearbeitet Auskunfts- und Löschanfragen.",
    },
    retireDialog: {
      title: "Konto stilllegen",
      question: "Möchten Sie das Konto von {{name}} stilllegen?",
      consequence:
        "Die Person kann sich nicht mehr anmelden; alle ihre Anmeldungen enden sofort. Das Konto bleibt als Urheber seiner Inhalte bestehen.",
    },
    reactivateDialog: {
      title: "Konto reaktivieren",
      question: "Möchten Sie das Konto von {{name}} reaktivieren?",
      consequence: "Die Person kann sich danach wieder anmelden; ihre Rollen gelten sofort wieder.",
    },
    lastAdmin: {
      title: "Letzter aktiver Administrator",
      lead:
        "Der achtstellige Code ist an die Adresse des Systemkontos gegangen. Mit ihm wird der Vorgang trotzdem ausgeführt.",
      code: "Code an das Systemkonto",
      submit: "Mit Code ausführen",
    },
    guardDialog: {
      title: "Aktion nicht möglich",
      confirm: "Verstanden",
    },
  },
  // The status of the installation (F-025, AC-045, NF-019).
  adminStatus: {
    title: "Status der Installation",
    lead:
      "Wie diese Installation eingestellt ist: die Festlegungen zu Zugang und Berechtigung mit ihren wirksamen Werten, die Rollen, die Rechtematrix und die Betriebsparameter.",
    module: "Modul {{module}} {{version}}",
    festlegungenTitle: "Festlegungen",
    colKey: "Festlegung",
    colQuestion: "Frage",
    colValue: "Wirksamer Wert",
    none: "keine",
    see: {
      roles: "siehe Rollen",
      relations: "siehe Beziehungen",
      matrix: "siehe Rechtematrix",
    },
    questions: {
      authentication_method: "Wie stellt das System fest, wer sich anmeldet?",
      authentication_account_creation: "Wie entsteht ein Konto?",
      session_absolute_limit: "Nach welcher Zeit endet eine Sitzung, unabhängig von ihrer Nutzung?",
      session_end: "Endet die Sitzung genau nach der Höchstdauer oder zur Mitternacht davor?",
      session_absolute_configurable: "Darf die Auslieferung die Höchstdauer ändern?",
      session_idle_limit: "Beendet Untätigkeit eine Sitzung, und nach welcher Zeit?",
      session_warning_lead: "Wie lange vor ihrem Ende wird eine Sitzung angekündigt?",
      protection_break_glass: "Wie kommt man in eine frische Installation, und wie zurück, wenn kein Administrator mehr handeln kann?",
      protection_step_up: "Verlangen kritische Vorgänge einen erneuten Nachweis der Person?",
      protection_step_up_extra: "Welche Vorgänge verlangen die Höherstufung zusätzlich?",
      authorization_scope: "Worauf bezieht sich eine Rollenzuweisung?",
      authorization_roles: "Welche Rollen gibt es?",
      authorization_administration: "Ist die Administration eine Rolle, und reicht sie an die Inhalte?",
      authorization_admin_role: "Welche Rolle ist die Administration?",
      authorization_groups: "Kann eine Berechtigung an eine Gruppe gehen?",
      authorization_account_visibility: "Welche anderen Konten sieht ein gewöhnlicher Benutzer?",
      authorization_relations: "Auf welche Beziehungen bezieht sich „eigene“?",
      authorization_matrix: "Welche Rolle darf welchen Vorgang?",
      lifecycle_removal: "Kann ein Konto gelöscht werden?",
    },
    rolesTitle: "Rollen",
    colRoleId: "Bezeichner",
    colRoleLabel: "Bezeichnung",
    administration: "Administration",
    relationsTitle: "Beziehungen",
    colRelation: "Beziehung",
    colMeaning: "Bedeutung",
    matrixTitle: "Rechtematrix",
    matrixLead: "✓ - die Rolle darf den Vorgang; – - sie darf ihn nicht; eigene - nur mit der genannten Beziehung.",
    colAction: "Vorgang",
    cell: {
      yes: "✓",
      no: "–",
      own: "eigene",
    },
    parametersTitle: "Betriebsparameter",
    parametersLead: "Jeder Parameter mit seinem wirksamen Wert und seiner Herkunft. Geheimnisse bleiben verdeckt.",
    colParameter: "Parameter",
    colOrigin: "Herkunft",
    originEnvironment: "Auslieferung",
    originDefault: "Voreinstellung",
    chatTitle: "Chat-Zustellung von Erinnerungen",
    chatConfigured: "Eingerichtet - Erinnerungen gehen auch an den Chat-Kanal.",
    chatNotConfigured: "Nicht eingerichtet - Erinnerungen erscheinen nur in der Anwendung.",
  },
  // The account journal (NF-017, AC-067).
  adminJournal: {
    title: "Kontoprotokoll",
    lead:
      "Jede Änderung an Konten und Rollen, jede Anmeldung, Abmeldung und Höherstufung und jeder abgelehnte Anmeldeversuch - mit handelnder Person, Zeitpunkt und Gegenstand. Nur fortschreibbar; nichts hier wird geändert oder gelöscht.",
    filter: "Protokoll filtern",
    account: "Konto",
    allAccounts: "Alle Einträge",
    empty: "Keine Einträge.",
    colAt: "Zeitpunkt",
    colActor: "Wer",
    colAction: "Vorgang",
    colSubject: "Gegenstand",
    colDetails: "Änderung",
    formerAccount: "ehemaliges Konto",
    session: "Sitzung {{id}}",
    changeRoles: "Rollen: {{from}} → {{to}}",
    changeState: "Zustand: {{from}} → {{to}}",
    changeAdmin: "Administration: {{from}} → {{to}}",
    createdRoles: "Rollen: {{roles}}",
    inviteSent: "Einladung versandt",
    inviteNotSent: "Einladung nicht versandt",
    displayNameChanged: "Anzeigename geändert",
    actions: {
      login: "Anmeldung",
      loginRefused: "Anmeldung abgelehnt",
      logout: "Abmeldung",
      systemLogin: "Anmeldung des Systemkontos",
      systemLoginRefused: "Anmeldung des Systemkontos abgelehnt",
      systemLoginCodeRefused: "Code des Systemkontos abgelehnt",
      firstLogin: "Erstanmeldung",
      reset: "Passwort zurückgesetzt",
      displayName: "Anzeigename geändert",
      password: "Passwort geändert",
      email: "Mailadresse geändert",
      create: "Konto angelegt",
      update: "Konto geändert",
      invite: "Einladung versandt",
      recover: "Mailadresse wiederhergestellt",
      anonymize: "Konto anonymisiert",
      sessionRevoke: "Anmeldung beendet",
      elevate: "Sitzung höhergestuft",
      elevateRefused: "Höherstufung abgelehnt",
    },
  },
  adminMasterData: {
    title: "Stammdaten",
    subtitle:
      "Kategorien, Bereiche, Schweregrade und Tags für die Vorfallerfassung verwalten.",
    auditHint:
      "Deaktivierte Einträge stehen bei neuen Vorfällen nicht mehr zur Auswahl, bleiben aber an bestehenden Vorfällen erhalten.",
    tabCategories: "Kategorien",
    tabAreas: "Bereiche",
    tabSeverities: "Schweregrade",
    tabTags: "Tags",
    listControls: "Suche und Filter",
    search: "Einträge durchsuchen",
    searchPlaceholder: "Name oder Beschreibung ...",
    statusFilter: "Status filtern",
    statusFilterOption: {
      all: "Alle Einträge",
      active: "Nur aktive",
      inactive: "Nur inaktive",
    },
    noMatch: "Kein Eintrag passt zu Suche und Filter.",
    tabStaleTags: "Pflegeverdächtige Tags",
    // GR-008 Wildwuchs-Erkennung.
    staleTags: {
      intro:
        "Aktive Tags, die seit mehr als 12 Monaten keinem Vorfall zugewiesen wurden - oder nie, obwohl sie älter als 12 Monate sind. Prüfen Sie, ob sie noch gebraucht werden; deaktivierte Tags bleiben an bestehenden Vorfällen erhalten.",
      empty: "Keine pflegeverdächtigen Tags.",
      neverUsed: "Nie verwendet",
    },
    showInactive: "Inaktive anzeigen",
    inactiveCount: "{{n}} inaktiv",
    create: "Neu",
    edit: "Bearbeiten",
    deactivate: "Deaktivieren",
    reactivate: "Reaktivieren",
    empty: "Noch keine Einträge vorhanden.",
    colName: "Name",
    colDescription: "Beschreibung",
    colRank: "Rang",
    colSymbol: "Symbol",
    colParent: "Übergeordnet",
    colResponsible: "Verantwortlich",
    colReactionHours: "Reaktionszeit",
    colColor: "Farbe",
    colCreatedAt: "Erstellt",
    colLastUsedAt: "Zuletzt verwendet",
    colUsageCount: "Verwendungen",
    colStatus: "Status",
    colActions: "Aktionen",
    statusActive: "Aktiv",
    statusInactive: "Inaktiv",
    hours: "{{hours}} h",
    optionNone: "Keine",
    createTitle: "Eintrag anlegen",
    editTitle: "Eintrag bearbeiten",
    dialogHint: "Der Name ist erforderlich. Alle übrigen Felder sind optional.",
    save: "Speichern",
    saving: "Wird gespeichert ...",
    created: "Eintrag angelegt.",
    updated: "Eintrag aktualisiert.",
    deactivated: "Eintrag deaktiviert.",
    reactivated: "Eintrag reaktiviert.",
    deactivateDialog: {
      title: "Eintrag deaktivieren",
      question: "Möchten Sie „{{name}}“ wirklich deaktivieren?",
      consequence:
        "Der Eintrag steht bei neuen Vorfällen nicht mehr zur Auswahl. Bestehende Vorfälle behalten ihn unverändert.",
    },
    reactivateDialog: {
      title: "Eintrag reaktivieren",
      question: "Möchten Sie „{{name}}“ wieder aktivieren?",
      consequence:
        "Der Eintrag steht danach bei neuen Vorfällen wieder zur Auswahl.",
    },
  },
  export: {
    csv: "CSV-Export",
    pdf: "Als PDF exportieren",
    running: "Wird erstellt ...",
    csvDone: "CSV-Export heruntergeladen.",
    pdfDone: "PDF-Export heruntergeladen.",
    error: "Der Export ist fehlgeschlagen.",
  },
  postmortem: {
    title: "Postmortem",
    eyebrow: "Postmortem - Vorfall {{number}}",
    resolvedOn: "Gelöst am {{date}}",
    backToIncident: "Zurück zum Vorfall",
    owner: "Incident Owner",
    notAssigned: "Nicht zugewiesen",
    editorIntro:
      "Strukturierte Nachbereitung des Vorfalls. Die vier Pflichtabschnitte sind für den Abschluss erforderlich.",
    mandatoryHeader: "Pflichtabschnitte",
    filledCounter: "{{filled}} von {{total}} befüllt",
    filled: "Befüllt",
    lessonsOptionalBadge: "Optional - blockiert den Abschluss nicht",
    optionalLabel: "(optional)",
    missingSectionsCallout:
      "Noch nicht ausgefüllt: {{sections}}. Der Vorfall lässt sich trotzdem abschließen; das Postmortem bleibt dann ein Entwurf.",
    draftSavedInline: "Entwurf gespeichert.",
    closedTitle: "Vorfall abgeschlossen",
    closedToIncident: "Zum Vorfall",
    closedToList: "Zur Vorfall-Liste",
    readOnlyHint: "Sie sehen dieses Postmortem nur lesend.",
    none: "Noch kein Postmortem vorhanden.",
    sections: {
      cause: "Ursache",
      affectedComponents: "Betroffene Komponenten",
      resolution: "Lösung",
      prevention: "Präventionsmaßnahme",
      positiveLearnings: "Was hat gut funktioniert?",
      earlierDetectable: "Was hätten wir früher erkennen können?",
    },
    lessonsTitle: "Lessons",
    optionalHint: "Optional - blockiert den Abschluss nicht.",
    validation: {
      causeRequired: "Bitte füllen Sie den Abschnitt Ursache aus.",
      affectedComponentsRequired:
        "Bitte füllen Sie den Abschnitt Betroffene Komponenten aus.",
      resolutionRequired: "Bitte füllen Sie den Abschnitt Lösung aus.",
      preventionRequired:
        "Bitte füllen Sie den Abschnitt Präventionsmaßnahme aus.",
    },
    saveDraft: "Entwurf speichern",
    saveFinal: "Final speichern",
    finalised: "Postmortem final gespeichert.",
    draftBadge: "Entwurf - unvollständig",
    draftHint:
      "Dieses Postmortem ist ein Entwurf. Unvollständige Pflichtabschnitte lassen sich jederzeit speichern; final gespeichert wird es erst, wenn alle ausgefüllt sind.",
    saving: "Wird gespeichert ...",
    close: "Vorfall abschließen",
    closing: "Wird abgeschlossen ...",
    saved: "Postmortem gespeichert.",
    closed: "Vorfall abgeschlossen.",
    tasks: {
      title: "Follow-up-Tasks",
      empty: "Noch keine Follow-up-Tasks vorhanden.",
      needsPostmortem:
        "Speichern Sie zuerst den Entwurf, um Follow-up-Tasks anzulegen.",
      fieldTitle: "Titel",
      fieldDescription: "Beschreibung (optional)",
      fieldAssignee: "Zugewiesen an",
      fieldDueDate: "Fälligkeitsdatum",
      selectPlaceholder: "Bitte wählen",
      add: "Task hinzufügen",
      adding: "Wird angelegt ...",
      added: "Follow-up-Task angelegt.",
      edit: "Bearbeiten",
      save: "Speichern",
      saved: "Follow-up-Task gespeichert.",
      statusChanged: "Task-Status geändert.",
      dueOn: "Fällig am",
    },
    followupStatus: {
      offen: "Offen",
      in_arbeit: "In Arbeit",
      erledigt: "Erledigt",
      verworfen: "Verworfen",
    },
    card: {
      open: "Postmortem anzeigen/bearbeiten",
      create: "Postmortem erstellen",
      none: "Noch kein Postmortem vorhanden.",
      complete: "Alle Pflichtabschnitte sind ausgefüllt.",
      incomplete: "{{count}} Pflichtabschnitt(e) noch offen.",
      draft: "Entwurf - noch nicht final gespeichert.",
    },
  },
  // What a Loupe entry states about the incident it was written on
  // (lib/loupe-context.ts) - shown in Loupe as a context line.
  loupe: {
    incident: "Vorfall",
    involvement: "Beteiligung",
    involvements: {
      owner: "Owner",
      assignee: "Bearbeiter",
      ownerAndAssignee: "Owner und Bearbeiter",
      none: "keine",
    },
  },
  // [NF-022] autosave of unsaved mask input. Deliberately not "Entwurf": in
  // the Postmortem-Maske that word names the F-007 state somebody saved.
  drafts: {
    bannerTitle: "Nicht gespeicherte Eingaben gefunden",
    bannerText:
      "Ihre Eingaben vom {{at}} wurden automatisch gesichert, aber noch nicht gespeichert. Möchten Sie damit weiterarbeiten?",
    restore: "Wiederherstellen",
    discard: "Verwerfen",
    statusSaving: "Eingaben werden automatisch gesichert ...",
    statusSaved: "Eingaben automatisch gesichert ({{at}}).",
    statusRetrying:
      "Eingaben konnten gerade nicht gesichert werden. Erneuter Versuch läuft - bitte die Seite geöffnet lassen.",
    statusFailed: "Eingaben konnten nicht automatisch gesichert werden.",
  },
} as const;

export type DeCatalog = typeof de;
